import * as React from 'react';
import { LocalForm, Control } from 'react-redux-form';
import { VendorViewModel, TradeViewModel, ExcludedZipViewModel } from '@models/Vendor';
import {
  TypeAheadComponent,
  Chip,
  CheckboxInline,
  ModalProps,
  ToastComponent,
  ToastType,
  ToastOptions
} from '@common/components';
import { PageViewModel } from '@models/common.models';
import { Option } from 'react-select';
import { ZipTripChargeComponent } from 'src/vendor/_components/vendor-trade-maintenance/_components/zip-trip-charge.component';
import ReactTable, { Column } from 'react-table';
import { toast } from 'react-toastify';

export interface TradeTripChargesComponentProperties {
  readonly vendorVM: VendorViewModel;
  readonly currentTrade: TradeViewModel;
  readonly zipCodes: Array<string>;
  readonly tripCharges: Array<ExcludedZipViewModel>;

  readonly getTripCharges: (currentTrade: TradeViewModel) => void;
  readonly zipCodeLookup: (
    vendorId: number,
    query: string,
    excludeZips?: boolean
  ) => Promise<PageViewModel<Option<string>>>;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly saveTripCharges: (currentTrade: TradeViewModel, tripCharges: Array<ExcludedZipViewModel>) => Promise<void>;
  readonly removeTripCharge: (item: ExcludedZipViewModel) => void;
}

interface TradeTripChargesComponentState {
  readonly excludeZips: boolean;
  readonly zipCodes: Array<string>;
}

const initialState = {
  excludeZips: false,
  zipCodes: []
};

export class TradeTripChargesComponent extends React.Component<
  TradeTripChargesComponentProperties,
  TradeTripChargesComponentState
> {
  constructor(props: TradeTripChargesComponentProperties) {
    super(props);
    this.state = initialState;
  }

  componentDidMount() {
    this.props.getTripCharges(this.props.currentTrade);
  }

  componentWillReceiveProps(nextProps: TradeTripChargesComponentProperties) {
    if (nextProps.currentTrade.tradeId && nextProps.currentTrade.tradeId !== this.props.currentTrade.tradeId) {
      this.props.getTripCharges(nextProps.currentTrade);
    }
  }

  handleAddZip = (zipCodeOption: Option<string>) => {
    if (!zipCodeOption.value) {
      return;
    }
    const found = this.state.zipCodes.find(zip => zip === zipCodeOption.value);
    found
      ? toast.warn(
          <ToastComponent
            type={ToastType.Warning}
            message={zipCodeOption.value + ' was already selected; please choose another zip code.'}
          />,
          ToastOptions.Warning
        )
      : this.setState({ zipCodes: [...this.state.zipCodes, zipCodeOption.value] });
  };

  handleRemoveZip = (zipCode: string) => {
    this.setState({ zipCodes: this.state.zipCodes.filter(zip => zip !== zipCode) });
  };

  handleExcludeZips = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({ excludeZips: event.currentTarget.checked });
  };

  openTripChargeModal = (item?: ExcludedZipViewModel) => {
    let zipTripCharges = item
      ? [{ ...item, tripCharge: item.tripCharge!.toFixed(2) }]
      : this.state.zipCodes.map((value: string, index: number, array: string[]) => {
          return {
            vendorId: this.props.currentTrade.vendorId,
            tradeId: this.props.currentTrade.tradeId,
            zip: value,
            tripCharge: 0
          } as ExcludedZipViewModel;
        });
    this.props.toggleModal({
      childProps: {
        // props to pass to the child
        zipTripCharges: zipTripCharges,
        submitForm: this.handleSaveTripCharges
      },
      closeLabel: 'Cancel',
      component: ZipTripChargeComponent,
      title: 'Edit Trip Charge'
    });
  };

  handleSaveTripCharges = (tripCharges: Array<ExcludedZipViewModel>) => {
    this.props.saveTripCharges(this.props.currentTrade, tripCharges).then(() => {
      this.setState({ zipCodes: [] });
    });
  };

  handleRemove = (item: ExcludedZipViewModel) => {
    this.props.toggleModal({
      title: 'Remove Trip Charge',
      isSmall: true,
      closeLabel: 'No',
      saveLabel: 'Yes',
      saveCallback: () => this.props.removeTripCharge(item),
      component: (props: any) => (
        <>
          <h2>Are you sure you want to remove this trip charge?</h2>
          <h3>Zip Code: {item.zip}</h3>
          <h3>Trip Charge Amount: ${item.tripCharge!.toFixed(2)}</h3>
        </>
      )
    });
  };

  render() {
    const columns: Column[] = [
      {
        accessor: 'zip',
        Header: 'Zip Code',
        sortable: true,
        Cell: (row: { original: ExcludedZipViewModel }) => (
          <a href="javascript:void(0);" onClick={() => this.openTripChargeModal(row.original)}>
            {row.original.zip}
          </a>
        )
      },
      {
        accessor: 'tripCharge',
        Header: 'Trip Charge',
        sortable: true,
        Cell: (props: { value?: number }) => <>{props.value ? '$' + props.value.toFixed(2) : '--'}</>
      },
      {
        id: 'remove',
        accessor: (row: ExcludedZipViewModel) => row,
        Cell: (row: { original: ExcludedZipViewModel }) => (
          <i className="fas fa-times red-text pointer" onClick={() => this.handleRemove(row.original)} />
        ),
        Header: 'Remove',
        maxWidth: 55,
        sortable: false
      }
    ];
    return (
      <>
        <h2>Trip Charges</h2>
        <LocalForm
          className="form-horizontal"
          hideNativeErrors={true}
          onSubmit={x => {
            this.openTripChargeModal();
          }}
        >
          <div className="row">
            <div className="col-xs-9 form-control">
              <label htmlFor={`.zipCodeQuery`}>Zip Code(s): </label>
              <Control.text
                className="mt-10"
                model=".zipCodes"
                component={(props: { value: ''; onChange: Function }) => {
                  return (
                    <TypeAheadComponent
                      {...props}
                      returnOption={true}
                      onChange={this.handleAddZip}
                      valueKey={'value'}
                      labelKey={'label'}
                      getSuggestions={(query: string) =>
                        this.props.zipCodeLookup(this.props.vendorVM.id, query, this.state.excludeZips)
                      }
                      value={props.value}
                      renderValue={(option: Option<string>) => {
                        return <div>{option.value}</div>;
                      }}
                    />
                  );
                }}
              />
              <ul className="list-unstyled">
                {this.state.zipCodes.map((zip: string) => (
                  <li key={zip} className="inline-block">
                    <Chip value={zip} handleRemove={this.handleRemoveZip} />
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-xs-3 form-control mt-25">
              <CheckboxInline>
                <Control.checkbox model=".excludeZips" onChange={e => this.handleExcludeZips(e)} />
                <label htmlFor={`.excludeZips`}>Show Covered Zips Only</label>
              </CheckboxInline>
            </div>
          </div>
          <div className="row center-xs">
            <button
              className="btn btn-primary"
              disabled={!this.state.zipCodes || this.state.zipCodes.length === 0}
              type="submit"
            >
              Submit
            </button>
          </div>
        </LocalForm>
        <ReactTable
          onPageChange={() => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          className="-striped -highlight"
          columns={columns}
          defaultPageSize={10}
          manual={true}
          data={this.props.tripCharges}
        />
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-trade-maintenance/_components/trade-trip-charges.component.tsx